import { useMemo } from "react";

import { Box, Heading, useColorModeValue, useTheme } from "@chakra-ui/react";
import {
  CartesianGrid,
  DotProps,
  Legend,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from "recharts";

import { PhHistoryEntry } from "~/api/ph";
import { formatDateWithYear } from "~/utils/date";

interface PhHistoryGraphProps {
  historyData: PhHistoryEntry[];
}

// Chart data interface for type safety
interface ChartDataPoint {
  date: string;
  timestamp: number; // Will be used as the x-axis value
  salivaPh: number;
  urinePh: number;
  weightedAvgPh: number;
  salivaDotFill: string;
  urineDotFill: string;
  avgDotFill: string;
}

// Extended DotProps to include the index property
interface CustomDotProps extends DotProps {
  index?: number;
}

export const PhHistoryGraph: React.FC<PhHistoryGraphProps> = ({ historyData }) => {
  const theme = useTheme();

  // Use Chakra's color mode values for theme-aware colors
  const gridColor = useColorModeValue("gray.300", "gray.600");
  const tickColor = useColorModeValue("gray.600", "gray.400");
  const chartBg = useColorModeValue("white", "gray.800");
  const textColor = useColorModeValue("gray.800", "gray.100");

  // Define consistent colors matching the PhMeasurementsChart with dark mode support
  const SALIVA_COLOR = useColorModeValue("#8884d8", "#a795ff"); // Purple for saliva, lighter in dark mode
  const URINE_COLOR = useColorModeValue("#ff8c00", "#ffac4d"); // Orange for urine, lighter in dark mode
  const AVG_COLOR = useColorModeValue("#2196f3", "#64b5f6"); // Blue for average, lighter in dark mode
  const OPTIMAL_PH = 6.4;
  const OPTIMAL_LINE_COLOR = useColorModeValue(theme.colors.green[500], theme.colors.green[300]);

  // Green color for pH axis labels and ticks
  const phAxisColor = useColorModeValue(theme.colors.green[600], theme.colors.green[300]);

  // Green color for date labels
  const dateColor = phAxisColor;

  // Green color for X-axis line
  const xAxisLineColor = phAxisColor;

  // Function to determine the color of the dot based on pH value
  // Matching the color schema from PhMeasurementsChart with dark mode support
  const getPhColor = (value: number): string => {
    // Use theme colors that adapt to color mode
    const redColor = theme.colors.red[useColorModeValue("500", "400")];
    const orangeColor = theme.colors.orange[useColorModeValue("400", "300")];
    const yellowColor = theme.colors.yellow[useColorModeValue("400", "300")];
    const greenColor = theme.colors.green[useColorModeValue("500", "400")];

    if (value < 4.8 || value > 8) return redColor;
    if (value < 6 || value > 6.8) return orangeColor;
    if (value < 6.1 || value > 6.7) return yellowColor;
    return greenColor;
  };

  // Calculate weighted average: (urinePh + 2*salivaPh)/3
  const calculateWeightedAverage = (urinePh: number, salivaPh: number): number => {
    return Number(((urinePh + 2 * salivaPh) / 3).toFixed(2));
  };

  // Format a timestamp for display
  const formatTimestampForDisplay = (timestamp: number): string => {
    const date = new Date(timestamp);
    return `${formatDateWithYear(date)} ${date.getHours().toString().padStart(2, "0")}:${date.getMinutes().toString().padStart(2, "0")}`;
  };

  // Format the data for the chart
  const chartData = useMemo<ChartDataPoint[]>(() => {
    // Sort data by testing date (oldest to newest)
    const sortedData = [...historyData].sort(
      (a, b) => new Date(a.testingDate).getTime() - new Date(b.testingDate).getTime()
    );

    return sortedData.map((entry) => {
      const weightedAvg = calculateWeightedAverage(entry.urinePh, entry.salivaPh);
      const timestamp = new Date(entry.testingDate).getTime();

      return {
        date: formatTimestampForDisplay(timestamp),
        timestamp: timestamp, // Used as the x-axis value for proportional spacing
        salivaPh: entry.salivaPh,
        urinePh: entry.urinePh,
        weightedAvgPh: weightedAvg,
        salivaDotFill: getPhColor(entry.salivaPh),
        urineDotFill: getPhColor(entry.urinePh),
        avgDotFill: getPhColor(weightedAvg)
      };
    });
  }, [historyData, theme.colors]);

  // Calculate min and max pH values to set graph boundaries
  const yDomain = useMemo(() => {
    if (chartData.length === 0) return [4, 8]; // Default range

    const allPhValues = chartData.flatMap((entry) => [entry.salivaPh, entry.urinePh, entry.weightedAvgPh]);

    let minPh = Math.min(...allPhValues);
    let maxPh = Math.max(...allPhValues);

    // Add some padding
    minPh = Math.max(0, Math.floor(minPh) - 0.5);
    maxPh = Math.min(14, Math.ceil(maxPh) + 0.5);

    return [minPh, maxPh];
  }, [chartData]);

  // Generate pH tick values for the Y axis
  const yAxisTicks = useMemo(() => {
    const [min, max] = yDomain;
    const ticks = [];
    // Create ticks at appropriate intervals based on the range
    const increment = max - min > 6 ? 1 : 0.5;

    for (let i = min; i <= max; i += increment) {
      ticks.push(i);
    }
    return ticks;
  }, [yDomain]);

  // Ensure 6.4 is included in the ticks for the optimal line alignment
  const adjustedYAxisTicks = useMemo(() => {
    const ticks = [...yAxisTicks];
    if (!ticks.includes(OPTIMAL_PH)) {
      ticks.push(OPTIMAL_PH);
      return ticks.sort((a, b) => a - b);
    }
    return ticks;
  }, [yAxisTicks, OPTIMAL_PH]);

  // Generate X-axis ticks based on timestamp data
  const xAxisTicks = useMemo(() => {
    if (chartData.length <= 1) return []; // Not enough data for meaningful ticks

    // Get min and max timestamps
    const minTime = Math.min(...chartData.map((d) => d.timestamp));
    const maxTime = Math.max(...chartData.map((d) => d.timestamp));

    // Determine a reasonable number of ticks based on data range
    const timeRange = maxTime - minTime;
    const DAY_MS = 86400000; // 24 hours in milliseconds

    // Adaptive tick generation based on total time span
    let numTicks = 5; // Default
    if (timeRange > DAY_MS * 30) {
      // More than a month
      numTicks = Math.min(8, chartData.length);
    } else if (timeRange > DAY_MS * 7) {
      // More than a week
      numTicks = Math.min(7, chartData.length);
    } else {
      numTicks = Math.min(6, chartData.length);
    }

    // Generate evenly distributed ticks
    const ticks = [];
    for (let i = 0; i < numTicks; i++) {
      const tickValue = minTime + (timeRange * i) / (numTicks - 1);
      ticks.push(Math.round(tickValue));
    }

    return ticks;
  }, [chartData]);

  if (historyData.length === 0) {
    return (
      <Box textAlign="center" py={8}>
        <Heading size="sm">No pH history data available to display</Heading>
      </Box>
    );
  }

  return (
    <Box mt={6}>
      <Heading size="md" mb={4} color={textColor}>
        pH Trends Over Time
      </Heading>
      <Box height="300px" position="relative" bg={chartBg} borderRadius="md" p={2} mb={4}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 50 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
            <XAxis
              dataKey="timestamp"
              type="number"
              domain={["dataMin", "dataMax"]}
              scale="time"
              ticks={xAxisTicks}
              angle={-45}
              textAnchor="end"
              height={80}
              tick={{
                fontSize: 11,
                fill: dateColor,
                fontWeight: "bold"
              }}
              tickFormatter={formatTimestampForDisplay}
              stroke={gridColor}
              dy={10}
              axisLine={{
                stroke: xAxisLineColor,
                strokeWidth: 1
              }}
              label={{
                value: "Date & Time",
                position: "bottom",
                offset: 20,
                fill: dateColor,
                fontSize: 14,
                fontWeight: "bold"
              }}
            />
            <YAxis
              domain={yDomain}
              ticks={adjustedYAxisTicks}
              stroke={phAxisColor}
              tick={{
                fill: phAxisColor,
                fontSize: 12,
                fontWeight: "bold"
              }}
              tickFormatter={(value) => value.toFixed(1)}
              label={{
                value: "pH Value",
                angle: -90,
                position: "insideLeft",
                style: {
                  textAnchor: "middle",
                  fill: phAxisColor,
                  fontSize: 14,
                  fontWeight: "bold"
                }
              }}
            />
            {/* ReferenceLine for the optimal pH value */}
            <ReferenceLine
              y={OPTIMAL_PH}
              stroke={OPTIMAL_LINE_COLOR}
              strokeDasharray="4 3"
              strokeWidth={2}
              opacity={0.75}
              ifOverflow="visible"
              label={{
                value: "Optimal pH: 6.4",
                position: "center",
                fill: OPTIMAL_LINE_COLOR,
                fontSize: 13,
                fontWeight: "bold",
                opacity: 0.5,
                dy: -10 // Move the label up above the line
              }}
            />
            <Tooltip
              formatter={(value: number | string, name: string) => {
                let label = name;
                if (name === "salivaPh") label = "Saliva pH";
                if (name === "urinePh") label = "Urine pH";
                if (name === "weightedAvgPh") label = "Weighted Avg pH";
                return [Number(value).toFixed(2), label];
              }}
              labelFormatter={(timestamp: number) => `Date: ${formatTimestampForDisplay(timestamp)}`}
              contentStyle={{
                backgroundColor: chartBg,
                borderColor: gridColor,
                color: textColor
              }}
              itemStyle={{ color: textColor }}
              labelStyle={{ color: textColor, fontWeight: "bold" }}
            />
            <Legend wrapperStyle={{ color: textColor }} verticalAlign="top" height={36} />
            <Line
              type="monotone"
              dataKey="salivaPh"
              stroke={SALIVA_COLOR}
              name="Saliva pH"
              strokeWidth={2}
              dot={(props: CustomDotProps) => {
                const { cx, cy, index } = props;
                const fillColor =
                  index !== undefined && chartData[index] ? chartData[index].salivaDotFill : SALIVA_COLOR;
                return <circle cx={cx} cy={cy} r={5} fill={fillColor} stroke={SALIVA_COLOR} strokeWidth={1} />;
              }}
              activeDot={{ r: 8 }}
            />
            <Line
              type="monotone"
              dataKey="urinePh"
              stroke={URINE_COLOR}
              name="Urine pH"
              strokeWidth={2}
              dot={(props: CustomDotProps) => {
                const { cx, cy, index } = props;
                const fillColor = index !== undefined && chartData[index] ? chartData[index].urineDotFill : URINE_COLOR;
                return <circle cx={cx} cy={cy} r={5} fill={fillColor} stroke={URINE_COLOR} strokeWidth={1} />;
              }}
              activeDot={{ r: 8 }}
            />
            <Line
              type="monotone"
              dataKey="weightedAvgPh"
              stroke={AVG_COLOR}
              name="Weighted Avg pH"
              strokeWidth={2}
              dot={(props: CustomDotProps) => {
                const { cx, cy, index } = props;
                const fillColor = index !== undefined && chartData[index] ? chartData[index].avgDotFill : AVG_COLOR;
                return <circle cx={cx} cy={cy} r={5} fill={fillColor} stroke={AVG_COLOR} strokeWidth={1} />;
              }}
              activeDot={{ r: 8 }}
            />
          </LineChart>
        </ResponsiveContainer>
      </Box>
    </Box>
  );
};

export default PhHistoryGraph;
